import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
	Box,
	Button,
	Container,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
	Snackbar,
	Alert,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Card,
	CardContent,
	Autocomplete,
	Chip,
	Checkbox,
	FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningIcon from "@mui/icons-material/Warning";
import SchoolIcon from "@mui/icons-material/School";
import { styled } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

// Default values in case settings can't be loaded
const DEFAULT_TRAVEL_EXPENSE_RATE = 10;
const DEFAULT_RECEIPT_THRESHOLD = 500;

const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

const createExpenseLine = () => ({
	id: uuidv4(),
	description: "",
	distance: "",
	amount: "",
	expenseType: "",
	receipt: null,
});

const createSchoolEntry = (schoolId) => ({
	id: uuidv4(),
	schoolId,
	expenses: [createExpenseLine()],
});

const EMPTY_SCHOOL_SELECTION = [];

const normalizeSchoolOptions = (schoolsData) => {
	const uniqueById = Array.from(
		new Map(
			(schoolsData || [])
				.filter((school) => school?.id)
				.map((school) => [school.id, school])
		).values()
	);

	const nameCounts = uniqueById.reduce((counts, school) => {
		const key = (school.name || "").trim().toLowerCase();
		counts[key] = (counts[key] || 0) + 1;
		return counts;
	}, {});

	return uniqueById
		.map((school) => {
			const name = school.name || "";
			const nameKey = name.trim().toLowerCase();
			const needsDistrict = nameCounts[nameKey] > 1 && school.district;

			return {
				...school,
				label: needsDistrict ? `${name} (${school.district})` : name,
			};
		})
		.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
};

const ExpenseTracker = () => {
	const { user, supabase } = useAuth();
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [confirmDialog, setConfirmDialog] = useState({
		open: false,
		title: "",
		message: "",
	});

	const [selectedDate, setSelectedDate] = useState(dayjs());
	const [isNoSchool, setIsNoSchool] = useState(false);
	const [schoolEntries, setSchoolEntries] = useState([]);
	const [errors, setErrors] = useState({});
	const [totalAmount, setTotalAmount] = useState(0);
	const [schoolVisits, setSchoolVisits] = useState([]);
	const [schools, setSchools] = useState([]);
	const [loadingSchoolData, setLoadingSchoolData] = useState(true);

	const [expenseTypes, setExpenseTypes] = useState([]);

	// Add useEffect to fetch expense types
	useEffect(() => {
		const fetchExpenseTypes = async () => {
			try {
				const { data, error } = await supabase
					.from("expense_types")
					.select("*")
					.eq("status", true)
					.order("name");

				if (error) throw error;
				setExpenseTypes(data || []);
			} catch (error) {
				console.error("Error fetching expense types:", error);
			}
		};

		fetchExpenseTypes();
	}, [supabase]);

	// Settings from the expense_settings table
	const [travelExpenseRate, setTravelExpenseRate] = useState(
		DEFAULT_TRAVEL_EXPENSE_RATE
	);
	const [receiptThreshold, setReceiptThreshold] = useState(
		DEFAULT_RECEIPT_THRESHOLD
	);
	const [settingsLoaded, setSettingsLoaded] = useState(false);

	// Fetch expense settings from the database
	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const { data, error } = await supabase
					.from("expense_settings")
					.select("setting_name, setting_value");

				if (error) {
					console.error("Error fetching expense settings:", error);
					return;
				}

				if (data && data.length > 0) {
					// Process settings data
					data.forEach((setting) => {
						if (setting.setting_name === "travel_expense_rate") {
							setTravelExpenseRate(
								parseFloat(setting.setting_value) || DEFAULT_TRAVEL_EXPENSE_RATE
							);
						} else if (setting.setting_name === "receipt_threshold") {
							setReceiptThreshold(
								parseFloat(setting.setting_value) || DEFAULT_RECEIPT_THRESHOLD
							);
						}
					});
				}
			} catch (error) {
				console.error("Error in fetchSettings:", error);
			} finally {
				setSettingsLoaded(true);
			}
		};

		fetchSettings();
	}, [supabase]);

	// Fetch school visits for the current user and all active schools
	useEffect(() => {
		const fetchSchoolVisitsAndSchools = async () => {
			setLoadingSchoolData(true);
			try {
				// First fetch school visits for this user
				const { data: visitsData, error: visitsError } = await supabase
					.from("school_visits")
					.select(
						`
						id, 
						planned_date, 
						visited_date,
						schools (
							id, 
							name
						)
					`
					)
					.eq("visited_by", user.id)
					.order("visited_date", { ascending: false });

				if (visitsError) throw visitsError;

				// Format the school visits data for the dropdown
				const formattedVisits = visitsData.map((visit) => ({
					id: visit.id,
					schoolName: visit.schools?.name || "Unknown School",
					visitDate: visit.visited_date || visit.planned_date,
					schoolId: visit.schools?.id,
					displayText: `${visit.schools?.name || "Unknown School"} - ${dayjs(
						visit.visited_date || visit.planned_date
					).format("DD/MM/YYYY")}`,
				}));

				setSchoolVisits(formattedVisits);

				// Fetch all active schools for the school selector
				const { data: schoolsData, error: schoolsError } = await supabase
					.from("schools")
					.select("id, name, district")
					.eq("status", "Active")
					.order("name");

				if (schoolsError) throw schoolsError;
				setSchools(normalizeSchoolOptions(schoolsData));
			} catch (error) {
				console.error("Error fetching school visits or schools:", error);
				setNotification({
					open: true,
					message: "Failed to load school visit data. Please try again later.",
					severity: "error",
				});
			} finally {
				setLoadingSchoolData(false);
			}
		};

		if (user) {
			fetchSchoolVisitsAndSchools();
		}
	}, [user, supabase]);

	// Calculate total amount whenever schoolEntries change
	useEffect(() => {
		const calculatedTotal = schoolEntries.reduce((sum, entry) => {
			const entryTotal = entry.expenses.reduce((entrySum, expense) => {
				return entrySum + (parseFloat(expense.amount) || 0);
			}, 0);
			return sum + entryTotal;
		}, 0);

		setTotalAmount(calculatedTotal);
	}, [schoolEntries]);

	// Update travel expense amounts when rate changes
	useEffect(() => {
		if (!settingsLoaded) return;

		const updatedEntries = schoolEntries.map((entry) => ({
			...entry,
			expenses: entry.expenses.map((expense) => {
				if (expense.expenseType === "Travel Expense" && expense.distance) {
					const calculatedAmount =
						parseFloat(expense.distance) * travelExpenseRate;
					return {
						...expense,
						amount: calculatedAmount.toString(),
					};
				}
				return expense;
			}),
		}));

		const hasChanges = updatedEntries.some((entry, schoolIndex) =>
			entry.expenses.some(
				(expense, expenseIndex) =>
					expense.amount !== schoolEntries[schoolIndex].expenses[expenseIndex].amount
			)
		);

		if (hasChanges) {
			setSchoolEntries(updatedEntries);
		}
	}, [settingsLoaded, travelExpenseRate]);

	// Check if receipt is required for a given expense
	const isReceiptRequired = useCallback(
		(expense) => {
			const expenseAmount = parseFloat(expense.amount) || 0;
			return expenseAmount > receiptThreshold;
		},
		[receiptThreshold]
	);

	const getSchoolName = useCallback(
		(schoolId) => {
			if (!schoolId) return "No school";
			const school = schools.find((s) => s.id === schoolId);
			return school?.label || school?.name || "School";
		},
		[schools]
	);

	const selectedSchools = useMemo(() => {
		if (schoolEntries.length === 0) {
			return EMPTY_SCHOOL_SELECTION;
		}

		return schoolEntries
			.map((entry) => schools.find((s) => s.id === entry.schoolId))
			.filter(Boolean);
	}, [schoolEntries, schools]);

	const filterSchoolOptions = useCallback((options, { inputValue }) => {
		const query = inputValue.trim().toLowerCase();
		if (!query) return options;

		return options.filter((option) => {
			const label = (option.label || option.name || "").toLowerCase();
			const district = (option.district || "").toLowerCase();
			return label.includes(query) || district.includes(query);
		});
	}, []);

	const findSchoolVisitForDate = useCallback(
		(schoolId) => {
			if (!selectedDate || !schoolId) return null;
			const dateStr = selectedDate.format("YYYY-MM-DD");
			return schoolVisits.find(
				(visit) =>
					visit.schoolId === schoolId &&
					dayjs(visit.visitDate).format("YYYY-MM-DD") === dateStr
			);
		},
		[selectedDate, schoolVisits]
	);

	const validateForm = useCallback(() => {
		const newErrors = {};
		let isValid = true;

		if (!selectedDate) {
			newErrors.date = "Date is required";
			isValid = false;
		}

		if (!isNoSchool && schoolEntries.length === 0) {
			newErrors.schools = "Select at least one school or choose No school";
			isValid = false;
		}

		if (isNoSchool && schoolEntries.length === 0) {
			newErrors.schools = "Add at least one expense";
			isValid = false;
		}

		schoolEntries.forEach((entry, schoolIndex) => {
			entry.expenses.forEach((expense, expenseIndex) => {
				const expenseKey = `${schoolIndex}-${expenseIndex}`;

				if (!expense.expenseType) {
					newErrors[`expenseType-${expenseKey}`] = "Expense type is required";
					isValid = false;
				}
				if (!expense.description) {
					newErrors[`description-${expenseKey}`] = "Description is required";
					isValid = false;
				}
				if (!expense.amount) {
					newErrors[`amount-${expenseKey}`] = "Amount is required";
					isValid = false;
				}
				if (isNaN(parseFloat(expense.amount))) {
					newErrors[`amount-${expenseKey}`] = "Amount must be a number";
					isValid = false;
				}
				if (expense.expenseType === "Travel Expense" && !expense.distance) {
					newErrors[`distance-${expenseKey}`] =
						"Distance is required for travel expenses";
					isValid = false;
				}
				if (expense.distance && isNaN(parseFloat(expense.distance))) {
					newErrors[`distance-${expenseKey}`] = "Distance must be a number";
					isValid = false;
				}

				const expenseAmount = parseFloat(expense.amount) || 0;
				if (
					expenseAmount > receiptThreshold &&
					!expense.receipt &&
					expense.expenseType !== "Travel Expense"
				) {
					newErrors[
						`receipt-${expenseKey}`
					] = `Receipt is required for amounts over ₹${receiptThreshold}`;
					isValid = false;
				}
			});
		});

		setErrors(newErrors);
		return isValid;
	}, [selectedDate, schoolEntries, receiptThreshold, isNoSchool]);

	const handleSelectedDateChange = (newDate) => {
		setSelectedDate(newDate);
	};

	const handleNoSchoolChange = (event) => {
		const checked = event.target.checked;
		setIsNoSchool(checked);

		if (checked) {
			setSchoolEntries([createSchoolEntry(null)]);
		} else {
			setSchoolEntries([]);
		}

		setErrors((prev) => {
			const next = { ...prev };
			delete next.schools;
			return next;
		});
	};

	const handleSchoolsChange = (_, selectedSchoolsList) => {
		if (isNoSchool) {
			setIsNoSchool(false);
		}

		const selectedIds = selectedSchoolsList.map((s) => s.id);

		setSchoolEntries((prev) => {
			const kept = prev.filter((entry) => selectedIds.includes(entry.schoolId));
			const existingIds = kept.map((e) => e.schoolId);
			const added = selectedSchoolsList
				.filter((school) => !existingIds.includes(school.id))
				.map((school) => createSchoolEntry(school.id));
			return [...kept, ...added];
		});
		setErrors((prev) => {
			const next = { ...prev };
			delete next.schools;
			return next;
		});
	};

	const handleExpenseInputChange = (schoolIndex, expenseIndex, field, value) => {
		const updatedEntries = [...schoolEntries];
		const oldExpense = {
			...updatedEntries[schoolIndex].expenses[expenseIndex],
		};
		updatedEntries[schoolIndex].expenses[expenseIndex] = {
			...oldExpense,
			[field]: value,
		};

		if (field === "distance" && oldExpense.expenseType === "Travel Expense") {
			const distance = parseFloat(value) || 0;
			updatedEntries[schoolIndex].expenses[expenseIndex].amount = (
				distance * travelExpenseRate
			).toString();
		} else if (field === "expenseType") {
			if (value === "Travel Expense") {
				if (oldExpense.distance) {
					const distance = parseFloat(oldExpense.distance) || 0;
					updatedEntries[schoolIndex].expenses[expenseIndex].amount = (
						distance * travelExpenseRate
					).toString();
				}
			} else if (oldExpense.expenseType === "Travel Expense") {
				updatedEntries[schoolIndex].expenses[expenseIndex].amount = "";
			}
		}

		setSchoolEntries(updatedEntries);
	};

	const handleFileChange = (schoolIndex, expenseIndex, e) => {
		if (e.target.files.length > 0) {
			handleExpenseInputChange(
				schoolIndex,
				expenseIndex,
				"receipt",
				e.target.files[0]
			);
		}
	};

	const handleCloseNotification = () => {
		setNotification((prev) => ({ ...prev, open: false }));
	};

	const removeSchoolEntry = (schoolIndex) => {
		setSchoolEntries((prev) => prev.filter((_, i) => i !== schoolIndex));
		setErrors({});
	};

	const addExpenseToSchool = (schoolIndex) => {
		const updatedEntries = [...schoolEntries];
		updatedEntries[schoolIndex].expenses.push(createExpenseLine());
		setSchoolEntries(updatedEntries);
	};

	const removeExpenseFromSchool = (schoolIndex, expenseIndex) => {
		const updatedEntries = [...schoolEntries];
		if (updatedEntries[schoolIndex].expenses.length <= 1) return;
		updatedEntries[schoolIndex].expenses.splice(expenseIndex, 1);
		setSchoolEntries(updatedEntries);

		const updatedErrors = { ...errors };
		Object.keys(updatedErrors).forEach((key) => {
			if (key.endsWith(`-${schoolIndex}-${expenseIndex}`)) {
				delete updatedErrors[key];
			}
		});
		setErrors(updatedErrors);
	};

	const getSchoolEntryTotal = (entry) =>
		entry.expenses.reduce(
			(sum, expense) => sum + (parseFloat(expense.amount) || 0),
			0
		);

	const getFlatExpensesForSave = () =>
		schoolEntries.flatMap((entry) =>
			entry.expenses.map((expense) => ({
				...expense,
				schoolId: entry.schoolId,
			}))
		);

	const uploadReceipt = async (file) => {
		try {
			// Check file type before attempting upload
			const fileType = file.type;
			const validTypes = [
				"image/jpeg",
				"image/png",
				"image/gif",
				"image/svg+xml",
				"application/pdf",
			];

			if (!validTypes.includes(fileType)) {
				throw new Error(
					`File type ${fileType} is not supported. Please upload JPEG, PNG, GIF, SVG, or PDF files.`
				);
			}

			const fileExt = file.name.split(".").pop();
			const fileName = `${uuidv4()}.${fileExt}`;
			const filePath = `receipts/${user.id}/${fileName}`;

			

			const { data, error: uploadError } = await supabase.storage
				.from("expenses")
				.upload(filePath, file, {
					contentType: file.type,
					cacheControl: "3600",
				});

			if (uploadError) throw uploadError;


			// Get the public URL
			const { data: urlData } = supabase.storage
				.from("expenses")
				.getPublicUrl(filePath);
			return urlData.publicUrl;
		} catch (error) {
			console.error("Error uploading receipt:", error);
			if (error.message && error.message.includes("mime type")) {
				// Provide more user-friendly error
				throw new Error(
					`The file type is not supported. Please upload JPEG, PNG, GIF, SVG, or PDF files only.`
				);
			}
			throw error;
		}
	};

	const handleSaveClick = () => {
		if (!validateForm()) return;

		const totalExpensesCount = getTotalExpensesCount();

		setConfirmDialog({
			open: true,
			title: "Confirm Save",
			message: `Are you sure you want to save ${totalExpensesCount} expense${
				totalExpensesCount > 1 ? "s" : ""
			} for ${selectedDate.format("DD/MM/YYYY")} with a total of ₹${totalAmount.toFixed(2)}?`,
		});
	};

	const handleConfirmSave = async () => {
		setLoading(true);
		setConfirmDialog((prev) => ({ ...prev, open: false }));

		const expensesToSave = getFlatExpensesForSave();
		const savedCount = expensesToSave.length;

		try {
			for (const expense of expensesToSave) {
				let receiptUrl = null;

				if (expense.receipt) {
					receiptUrl = await uploadReceipt(expense.receipt);
				}

				const matchingVisit = findSchoolVisitForDate(expense.schoolId);

				const { error } = await supabase.from("expenses").insert({
					user_id: user.id,
					date: selectedDate.format("YYYY-MM-DD"),
					description: expense.description,
					distance: expense.distance || null,
					amount: parseFloat(expense.amount),
					expense_type: expense.expenseType,
					receipt_url: receiptUrl,
					school_visit_id: expense.schoolId ? matchingVisit?.id || null : null,
					school_id: expense.schoolId || null,
					status: "Pending Approval",
				});

				if (error) throw error;
			}

			setSelectedDate(dayjs());
			setIsNoSchool(false);
			setSchoolEntries([]);

			setNotification({
				open: true,
				message: `${savedCount} expense${
					savedCount > 1 ? "s" : ""
				} saved successfully!`,
				severity: "success",
			});
		} catch (error) {
			console.error("Error saving expenses:", error);
			setNotification({
				open: true,
				message: `Error: ${error.message || "Failed to save expenses"}`,
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCancelSave = () => {
		setConfirmDialog((prev) => ({ ...prev, open: false }));
	};

	const getTotalExpensesCount = () =>
		schoolEntries.reduce((sum, entry) => sum + entry.expenses.length, 0);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Container>
				<Typography variant="h4" component="h1">
					Expense Tracker
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Select a date, choose one or more schools (or mark as no school), then
					enter expense details. For travel expenses, the amount is calculated at ₹
					{travelExpenseRate} per KM.
					{receiptThreshold > 0 && (
						<> Receipt is required for expenses over ₹{receiptThreshold}.</>
					)}
				</Typography>

				{loadingSchoolData && (
					<Alert severity="info" sx={{ mb: 3 }}>
						Loading school visit data...
					</Alert>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSaveClick();
					}}>
					{/* Step 1: Date */}
					<Card sx={{ mb: 3 }}>
						<CardContent>
							<Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
								1. Select date
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<DatePicker
										label="Date *"
										value={selectedDate}
										onChange={handleSelectedDateChange}
										maxDate={dayjs()}
										sx={{ width: "100%" }}
										slotProps={{
											textField: {
												fullWidth: true,
												error: !!errors.date,
												helperText: errors.date,
												size: "small",
											},
										}}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>

					{/* Step 2: Schools */}
					<Card sx={{ mb: 3 }}>
						<CardContent>
							<Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
								2. Select schools
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={isNoSchool}
										onChange={handleNoSchoolChange}
										disabled={loadingSchoolData}
									/>
								}
								label="No school"
								sx={{ mb: 2, display: "block" }}
							/>
							<Autocomplete
								multiple
								options={schools}
								getOptionLabel={(option) => option.label || option.name || ""}
								getOptionKey={(option) => option.id}
								value={selectedSchools}
								onChange={handleSchoolsChange}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								filterOptions={filterSchoolOptions}
								filterSelectedOptions
								disableCloseOnSelect
								disabled={loadingSchoolData || isNoSchool}
								noOptionsText="No schools found"
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip
											{...getTagProps({ index })}
											key={option.id}
											label={option.label || option.name}
											size="small"
										/>
									))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Schools *"
										placeholder={
											schools.length > 0
												? "Select one or more schools"
												: "No schools available"
										}
										error={!!errors.schools}
										helperText={
											errors.schools ||
											(isNoSchool
												? "Expense details will be entered without a school"
												: "Choose all schools you visited on this date")
										}
										size="small"
									/>
								)}
							/>
						</CardContent>
					</Card>

					{/* Step 3: Expense details per school */}
					{schoolEntries.length > 0 && (
						<Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
							3. Enter expense details
							{isNoSchool ? "" : " for each school"}
						</Typography>
					)}

					{schoolEntries.map((entry, schoolIndex) => (
						<Card key={entry.id} sx={{ mb: 3 }}>
							<CardContent>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 2,
									}}>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<SchoolIcon sx={{ mr: 1, color: "primary.main" }} />
										<Typography variant="h6">
											{getSchoolName(entry.schoolId)}
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
											({entry.expenses.length} expense
											{entry.expenses.length > 1 ? "s" : ""} · ₹
											{getSchoolEntryTotal(entry).toFixed(2)})
										</Typography>
									</Box>
									{!isNoSchool && (
										<IconButton
											size="small"
											color="error"
											onClick={() => removeSchoolEntry(schoolIndex)}
											aria-label="Remove school">
											<DeleteIcon fontSize="small" />
										</IconButton>
									)}
								</Box>

								{entry.schoolId && findSchoolVisitForDate(entry.schoolId) && (
									<Alert severity="info" sx={{ mb: 2 }}>
										Linked to your school visit on this date.
									</Alert>
								)}

								{entry.expenses.map((expense, expenseIndex) => (
									<Paper
										key={expense.id}
										elevation={0}
										sx={{
											mb: 2,
											p: 2,
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 1,
										}}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 2,
											}}>
											<Typography variant="subtitle2">
												Expense #{expenseIndex + 1}
											</Typography>
											{entry.expenses.length > 1 && (
												<IconButton
													size="small"
													color="error"
													onClick={() =>
														removeExpenseFromSchool(schoolIndex, expenseIndex)
													}
													aria-label="Remove expense">
													<DeleteIcon fontSize="small" />
												</IconButton>
											)}
										</Box>

										<Grid container spacing={2}>
											<Grid item xs={12} md={6}>
												<FormControl
													fullWidth
													error={
														!!errors[`expenseType-${schoolIndex}-${expenseIndex}`]
													}
													size="small">
													<InputLabel
														id={`expense-type-label-${schoolIndex}-${expenseIndex}`}>
														Expense Type *
													</InputLabel>
													<Select
														labelId={`expense-type-label-${schoolIndex}-${expenseIndex}`}
														value={expense.expenseType}
														onChange={(e) =>
															handleExpenseInputChange(
																schoolIndex,
																expenseIndex,
																"expenseType",
																e.target.value
															)
														}
														label="Expense Type *">
														{expenseTypes.map((type) => (
															<MenuItem key={type.id} value={type.name}>
																{type.name}
															</MenuItem>
														))}
													</Select>
													{errors[`expenseType-${schoolIndex}-${expenseIndex}`] && (
														<Typography variant="caption" color="error">
															{errors[`expenseType-${schoolIndex}-${expenseIndex}`]}
														</Typography>
													)}
												</FormControl>
											</Grid>

											<Grid item xs={12} md={6}>
												{expense.expenseType === "Travel Expense" ? (
													<TextField
														label="Distance (KM) *"
														fullWidth
														value={expense.distance}
														onChange={(e) =>
															handleExpenseInputChange(
																schoolIndex,
																expenseIndex,
																"distance",
																e.target.value
															)
														}
														error={
															!!errors[`distance-${schoolIndex}-${expenseIndex}`]
														}
														helperText={
															errors[`distance-${schoolIndex}-${expenseIndex}`] ||
															`Amount will be calculated at ₹${travelExpenseRate} per KM`
														}
														size="small"
													/>
												) : (
													<TextField
														label="Amount (₹) *"
														fullWidth
														value={expense.amount}
														onChange={(e) =>
															handleExpenseInputChange(
																schoolIndex,
																expenseIndex,
																"amount",
																e.target.value
															)
														}
														error={
															!!errors[`amount-${schoolIndex}-${expenseIndex}`]
														}
														helperText={
															errors[`amount-${schoolIndex}-${expenseIndex}`]
														}
														size="small"
														InputProps={{
															startAdornment: (
																<InputAdornment position="start">₹</InputAdornment>
															),
														}}
													/>
												)}
											</Grid>

											<Grid item xs={12} md={8}>
												<TextField
													label="Description *"
													fullWidth
													value={expense.description}
													onChange={(e) =>
														handleExpenseInputChange(
															schoolIndex,
															expenseIndex,
															"description",
															e.target.value
														)
													}
													error={
														!!errors[`description-${schoolIndex}-${expenseIndex}`]
													}
													helperText={
														errors[`description-${schoolIndex}-${expenseIndex}`]
													}
													size="small"
												/>
											</Grid>

											<Grid item xs={12} md={4}>
												<Box>
													<Button
														component="label"
														variant="outlined"
														startIcon={<CloudUploadIcon />}
														size="small"
														fullWidth
														color="primary"
														sx={{ height: "40px" }}>
														{expense.receipt
															? expense.receipt.name.length > 20
																? expense.receipt.name.substring(0, 20) + "..."
																: expense.receipt.name
															: expense.expenseType === "Travel Expense"
															? "Upload Receipt"
															: isReceiptRequired(expense)
															? "Upload Receipt (Required) *"
															: "Upload Receipt"}
														<VisuallyHiddenInput
															type="file"
															accept="image/*,.pdf"
															onChange={(e) =>
																handleFileChange(schoolIndex, expenseIndex, e)
															}
														/>
													</Button>
													{errors[`receipt-${schoolIndex}-${expenseIndex}`] && (
														<Typography
															variant="caption"
															color="error"
															sx={{
																display: "flex",
																alignItems: "center",
																mt: 0.5,
															}}>
															<WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
															{errors[`receipt-${schoolIndex}-${expenseIndex}`]}
														</Typography>
													)}
													{!errors[`receipt-${schoolIndex}-${expenseIndex}`] &&
														isReceiptRequired(expense) &&
														!expense.receipt &&
														expense.expenseType !== "Travel Expense" && (
															<Typography
																variant="caption"
																color="error"
																sx={{
																	display: "flex",
																	alignItems: "center",
																	mt: 0.5,
																}}>
																<WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
																Receipt required for amounts over ₹
																{receiptThreshold}
															</Typography>
														)}
												</Box>
											</Grid>
										</Grid>
									</Paper>
								))}

								<Button
									variant="outlined"
									startIcon={<AddIcon />}
									onClick={() => addExpenseToSchool(schoolIndex)}
									size="small">
									Add Expense
								</Button>
							</CardContent>
						</Card>
					))}

					{/* Total amount summary */}
					<Paper
						elevation={0}
						sx={{
							p: 2,
							mb: 3,
							bgcolor: "primary.light",
							color: "primary.contrastText",
							borderRadius: 1,
						}}>
						<Grid container alignItems="center">
							<Grid item xs={12} md={8}>
								<Typography variant="body1" fontWeight="medium">
									Total Expense Amount
								</Typography>
								<Typography variant="caption">
									{selectedDate
										? `For ${selectedDate.format("DD/MM/YYYY")} · `
										: ""}
									{getTotalExpensesCount()}{" "}
									{getTotalExpensesCount() === 1 ? "expense" : "expenses"}
									{isNoSchool
										? " (no school)"
										: ` across ${schoolEntries.length} ${
												schoolEntries.length === 1 ? "school" : "schools"
										  }`}
								</Typography>
							</Grid>
							<Grid item xs={12} md={4}>
								<Typography variant="h5" align="right" fontWeight="bold">
									₹{totalAmount.toFixed(2)}
								</Typography>
							</Grid>
						</Grid>
					</Paper>

					{/* Actions row */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							flexWrap: "wrap",
							gap: 2,
							mb: 4,
						}}>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								variant="outlined"
								onClick={() => {
									setSelectedDate(dayjs());
									setIsNoSchool(false);
									setSchoolEntries([]);
									setErrors({});
								}}
								disabled={loading}>
								Clear All
							</Button>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								disabled={loading}>
								{loading ? (
									<CircularProgress size={24} />
								) : getTotalExpensesCount() > 1 ? (
									`Save ${getTotalExpensesCount()} Expenses`
								) : (
									"Save Expense"
								)}
							</Button>
						</Box>
					</Box>
				</form>

				{/* Confirmation Dialog */}
				<Dialog open={confirmDialog.open} onClose={handleCancelSave}>
					<DialogTitle>{confirmDialog.title}</DialogTitle>
					<DialogContent>
						<DialogContentText>{confirmDialog.message}</DialogContentText>

						{getTotalExpensesCount() > 0 && (
							<TableContainer sx={{ mt: 2 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>School</TableCell>
											<TableCell>Expense Type</TableCell>
											<TableCell align="right">Amount (₹)</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{schoolEntries.flatMap((entry) =>
											entry.expenses.map((expense) => (
												<TableRow key={expense.id}>
													<TableCell>{getSchoolName(entry.schoolId)}</TableCell>
													<TableCell>{expense.expenseType || "—"}</TableCell>
													<TableCell align="right">
														{(parseFloat(expense.amount) || 0).toFixed(2)}
													</TableCell>
												</TableRow>
											))
										)}
										<TableRow>
											<TableCell
												colSpan={2}
												align="right"
												sx={{ fontWeight: "bold" }}>
												Total:
											</TableCell>
											<TableCell align="right" sx={{ fontWeight: "bold" }}>
												{totalAmount.toFixed(2)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCancelSave} color="primary">
							Cancel
						</Button>
						<Button
							onClick={handleConfirmSave}
							color="primary"
							variant="contained">
							Confirm
						</Button>
					</DialogActions>
				</Dialog>

				{/* Notification */}
				<Snackbar
					open={notification.open}
					autoHideDuration={6000}
					onClose={handleCloseNotification}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
					<Alert
						onClose={handleCloseNotification}
						severity={notification.severity}
						variant="filled">
						{notification.message}
					</Alert>
				</Snackbar>
			</Container>
		</LocalizationProvider>
	);
};

export default ExpenseTracker;
